import {getTestProps} from '@benchling/js-test-utils';
import {useId} from '@mantine/hooks';
import React from 'react';

import AaSequenceConstants, {
  PRECISION_ISOELECTRIC_POINT,
  getMolecularWeightPrecision,
} from 'molbio/aa-sequences/util/AaSequenceConstants';
import * as ProteinProperties from 'molbio/aa-sequences/util/AaSequenceProperties';
import * as sequtil from 'molbio/SeqUtil';
import {aaSequenceSelectionUIGlobalState} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';

export interface AaSequenceStatusBarProps {
  proteinId: string;
  aminoAcids: string;
}

function AaSequenceStatusBar(props: AaSequenceStatusBarProps): JSX.Element {
  const [selection] = aaSequenceSelectionUIGlobalState.useGlobalClientState(props.proteinId);
  const labelIdPrefix = useId();
  const aminoAcidsLabelId = `${labelIdPrefix}-amino-acids`;
  const startLabelId = `${labelIdPrefix}-start`;
  const endLabelId = `${labelIdPrefix}-end`;
  const lengthLabelId = `${labelIdPrefix}-length`;
  const mwLabelId = `${labelIdPrefix}-mw`;
  const pILabelId = `${labelIdPrefix}-pi`;
  const insertLabelId = `${labelIdPrefix}-insert`;

  function renderAaSequenceSelectionStatus(): JSX.Element {
    const aminoAcids = sequtil.residueSlice(
      props.aminoAcids,
      selection.start,
      selection.end ?? undefined,
      false
    );
    const [start, end] = sequtil.modelRangeToDisplay(
      selection.start,
      selection.end,
      aminoAcids.length,
      AaSequenceConstants.DEFAULT_START_INDEX
    );
    if (selection.end != null) {
      return (
        <>
          <div className="statusbar-group" key="start">
            <div className="statusbar-label" id={startLabelId}>
              Start
            </div>
            <div
              aria-labelledby={startLabelId}
              className="statusbar-field selectable"
              {...getTestProps({component: 'AaSequenceStatusBar', element: 'start'})}
            >
              {start}
            </div>
          </div>
          <div className="statusbar-group" key="end">
            <div className="statusbar-label" id={endLabelId}>
              End
            </div>
            <div
              aria-labelledby={endLabelId}
              className="statusbar-field selectable"
              {...getTestProps({component: 'AaSequenceStatusBar', element: 'end'})}
            >
              {end}
            </div>
          </div>
          <div className="statusbar-group" key="length">
            <div className="statusbar-label" id={lengthLabelId}>
              Length
            </div>
            <div
              aria-labelledby={lengthLabelId}
              className="statusbar-field selectable"
              {...getTestProps({component: 'AaSequenceStatusBar', element: 'length'})}
            >
              {aminoAcids.length}
            </div>
          </div>
          <div className="statusbar-group" key="mw">
            <div className="statusbar-label" id={mwLabelId}>
              MW
            </div>
            <div
              aria-labelledby={mwLabelId}
              className="statusbar-field selectable"
              {...getTestProps({component: 'AaSequenceStatusBar', element: 'molecularWeight'})}
            >
              {ProteinProperties.getMolecularWeight(aminoAcids).toFixed(getMolecularWeightPrecision())}
            </div>
          </div>
          <div className="statusbar-group" key="pI">
            <div className="statusbar-label" id={pILabelId}>
              pI
            </div>
            <div
              aria-labelledby={pILabelId}
              className="statusbar-field selectable"
              {...getTestProps({component: 'AaSequenceStatusBar', element: 'isoelectricPoint'})}
            >
              {ProteinProperties.getIsoelectricPoint(aminoAcids).toFixed(PRECISION_ISOELECTRIC_POINT)}
            </div>
          </div>
        </>
      );
    }
    return (
      <div className="statusbar-group">
        <div className="statusbar-label" id={insertLabelId}>
          Insert
        </div>
        <div
          aria-labelledby={insertLabelId}
          className="statusbar-field selectable"
          {...getTestProps({component: 'AaSequenceStatusBar', element: 'insert'})}
        >
          {start}
        </div>
      </div>
    );
  }

  return (
    <div {...getTestProps({component: 'AaSequenceStatusBar', element: 'root'})}>
      <div className="statusbar-section">
        <div className="statusbar-group">
          <div className="statusbar-label" id={aminoAcidsLabelId}>
            Amino Acids
          </div>
          <div
            aria-labelledby={aminoAcidsLabelId}
            className="statusbar-field selectable"
            {...getTestProps({component: 'AaSequenceStatusBar', element: 'aminoAcidsCount'})}
          >
            {props.aminoAcids.length}
          </div>
        </div>
      </div>
      <div className="statusbar-section">{renderAaSequenceSelectionStatus()}</div>
    </div>
  );
}

export default AaSequenceStatusBar;
